import {
  useEffect,
  useCallback,
  useRef,
  useMemo,
  type CSSProperties,
} from "react";
import { preload } from "react-dom";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  selectAdminIsImageLoaded,
  setAdminBackgroundUrl,
  setAdminImageLoaded,
} from "@/store/slices/adminBackgroundSlice/adminBackgroundSlice";
import {
  ADMIN_DEFAULT_BACKGROUND_URL,
  ADMIN_OVERLAY_URL,
  ADMIN_FALLBACK_COLOR,
  ADMIN_BG_OPACITY,
  ADMIN_OVERLAY_OPACITY,
  ADMIN_TRANSITION_DURATION,
} from "@/layouts/AdminBackgroundLayout/AdminBackgroundLayout.types";
import { logger } from "../utils";

import { settingsResource } from "@/features/settings/api/settingsResource";

interface UseAdminBackgroundReturn {
  backgroundStyles: CSSProperties;
  backgroundImageUrl: string | null;
  effectiveUrl: string;
  fallbackColor: string;
  isImageLoaded: boolean;
  isFetching: boolean;
  isUploading: boolean;
  isRemoving: boolean;
  isUsingCustomBackground: boolean;
  upload: (file: File) => Promise<void>;
  remove: () => Promise<void>;
}

// ✅ Helper: Validate URL is a real, usable URL
const isValidUrl = (url: unknown): url is string => {
  if (typeof url !== "string") return false;
  if (url.trim() === "") return false;
  return true;
};
export const useAdminBackground = (): UseAdminBackgroundReturn => {
  const dispatch = useAppDispatch();
  const imageRef = useRef<HTMLImageElement | null>(null);

  // ✅ Add isSuccess
  const {
    data,
    isLoading: isFetching,
    isSuccess,
    isError,
  } = settingsResource.hooks.useGetBackgroundImage();

  logger.info("imageData", data?.url);
  logger.info("isSuccess", isSuccess);
  logger.info("isError", isError);

  // ✅ Only extract URL if API returned success AND url is valid
  const customUrl = useMemo(() => {
    if (!isSuccess) return null; // API not succeeded yet
    if (isError) return null; // API errored
    if (!isValidUrl(data?.url)) return null; // No valid url

    return data.url;
  }, [isSuccess, isError, data?.url]);

  const isImageLoaded = useAppSelector(selectAdminIsImageLoaded);

  // ✅ Only compute effectiveUrl AFTER API has succeeded
  const effectiveUrl = useMemo(() => {
    // Still loading
    if (isFetching) {
      logger.info("⏳ API still loading, using default background");
      return ADMIN_DEFAULT_BACKGROUND_URL;
    }

    // API errored
    if (isError) {
      logger.warn("⚠️ API errored, using default background");
      return ADMIN_DEFAULT_BACKGROUND_URL;
    }

    // API succeeded with custom url
    if (isSuccess && customUrl) {
      logger.info("🖼️ API succeeded, using custom background:", customUrl);
      return customUrl;
    }

    // API succeeded but no custom url
    logger.info("🏔️ API succeeded, no custom background, using default");
    return ADMIN_DEFAULT_BACKGROUND_URL;
  }, [customUrl, isFetching, isSuccess, isError]);

  // ✅ Sync to Redux ONLY when API succeeds
  useEffect(() => {
    if (isSuccess && customUrl) {
      logger.info("📦 Syncing custom URL to Redux:", customUrl);
      dispatch(setAdminBackgroundUrl(customUrl));
    }

    if (isSuccess && !customUrl) {
      logger.info("📦 No custom URL, clearing Redux");
      dispatch(setAdminBackgroundUrl(null));
    }
  }, [isSuccess, customUrl, dispatch]);

  // Load image - only when API is done and effectiveUrl changes
  useEffect(() => {
    if (isFetching) return; // Wait for API
    if (!isSuccess && !isError) return; // Wait for definitive state
    if (!effectiveUrl) return;

    // Cleanup previous image
    if (imageRef.current) {
      imageRef.current.onload = null;
      imageRef.current.onerror = null;
      imageRef.current.src = "";
      imageRef.current = null;
    }

    dispatch(setAdminImageLoaded(false));

    preload(effectiveUrl, {
      as: "image",
      fetchPriority: "high",
      crossOrigin: "anonymous",
    });

    const img = new Image();
    imageRef.current = img;
    img.crossOrigin = "anonymous";

    img.onload = () => {
      logger.info("✅ Image loaded:", effectiveUrl);
      dispatch(setAdminImageLoaded(true));
    };

    img.onerror = (err) => {
      logger.error("❌ Image failed to load:", effectiveUrl, err);
      dispatch(setAdminImageLoaded(false));

      if (effectiveUrl !== ADMIN_DEFAULT_BACKGROUND_URL) {
        logger.info("🔄 Falling back to default background");
        dispatch(setAdminBackgroundUrl(null));
      }
    };

    img.src = effectiveUrl;

    return () => {
      if (imageRef.current) {
        imageRef.current.onload = null;
        imageRef.current.onerror = null;
        imageRef.current.src = "";
        imageRef.current = null;
      }
    };
  }, [effectiveUrl, isFetching, isSuccess, isError, dispatch]);

  // CSS variables
  const backgroundStyles = useMemo(
    (): CSSProperties =>
      ({
        "--admin-fallback-color": ADMIN_FALLBACK_COLOR,
        "--admin-bg-image": isImageLoaded ? `url(${effectiveUrl})` : "none",
        "--admin-bg-opacity": isImageLoaded ? ADMIN_BG_OPACITY : 0,
        "--admin-overlay-image": `url(${ADMIN_OVERLAY_URL})`,
        "--admin-overlay-opacity": ADMIN_OVERLAY_OPACITY,
        "--admin-transition-duration": `${ADMIN_TRANSITION_DURATION}`,
      }) as CSSProperties,
    [effectiveUrl, isImageLoaded],
  );

  const upload = useCallback(async (file: File): Promise<void> => {
    logger.info("Uploading file:", file.name);
    throw new Error("Upload not implemented yet");
  }, []);

  const remove = useCallback(async (): Promise<void> => {
    throw new Error("Remove not implemented yet");
  }, []);

  return {
    backgroundStyles,
    backgroundImageUrl: isImageLoaded ? effectiveUrl : null,
    effectiveUrl,
    fallbackColor: ADMIN_FALLBACK_COLOR,
    isImageLoaded,
    isFetching,
    isUploading: false,
    isRemoving: false,
    isUsingCustomBackground: isSuccess && customUrl !== null, // ✅ both must be true
    upload,
    remove,
  };
};
